import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Users, TrendingUp, Brain, Shield, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
// import { getPageContent } from '../utils/contentManager'; // ❌ Temporarily not needed for backend fetch

const Home = () => {
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("http://localhost:5000/api/home")
      .then(res => res.json())
      .then(data => {
        setPageContent(data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-xl">
        Loading...
      </div>
    );
  }

  const features = [
    {
      icon: Brain,
      title: 'Agentic AI Solutions',
      description: 'Implement cutting-edge AI agents that autonomously handle complex business processes and decision-making.'
    },
    {
      icon: Shield,
      title: 'IT Consulting',
      description: 'Comprehensive technology strategy and implementation to modernize your business infrastructure.'
    },
    {
      icon: Target,
      title: 'Business Strategy',
      description: 'Data-driven strategic planning to accelerate growth and optimize operational efficiency.'
    }
  ];

  const stats = [
    { number: '50+', label: 'Clients Transformed' },
    { number: '200%', label: 'Average ROI Increase' },
    { number: '24/7', label: 'AI-Powered Support' },
    { number: '99.9%', label: 'System Uptime' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStart Inc.',
      text: 'OHA System revolutionized our operations with their AI solutions. We saw 300% productivity increase within 3 months.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Michael Chen',
      company: 'GrowthCorp',
      text: 'Their business strategy consulting helped us scale from startup to $10M revenue. Exceptional expertise and results.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
    },
    {
      name: 'Emily Rodriguez',
      company: 'InnovateLab',
      text: 'The AI implementation was seamless. Their team understood our needs and delivered beyond expectations.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 102, 255, 0.8), rgba(0, 51, 153, 0.8)), url('https://images.unsplash.com/photo-1601132359864-c974e79890ac?w=1920&h=1080&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              {pageContent.heroTitle || 'Transform Your Business with'}
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-white">
                {pageContent.heroTitle ? '' : 'Agentic AI Solutions'}
              </span>
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed text-indigo-100"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              {pageContent.heroSubtitle || 'Empowering small businesses and startups with cutting-edge AI technology, strategic consulting, and innovative solutions that drive unprecedented growth.'}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(255, 255, 255, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-800 px-8 py-4 rounded-xl font-bold text-lg shadow-premium hover:shadow-glow transition-all duration-300 flex items-center space-x-2"
              >
                <span>{pageContent.ctaText || 'Get Free Basic Consultation'}</span>
                <ArrowRight size={24} />
              </motion.button>
              <Link
                to="/services"
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-blue-800 transition-all duration-300"
              >
                Explore Our Services
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating Elements */}
        <motion.div
          animate={{ y: [-20, 20, -20] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full backdrop-blur-lg"
        />
        <motion.div
          animate={{ y: [20, -20, 20] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-32 right-16 w-32 h-32 bg-indigo-400/20 rounded-full backdrop-blur-lg"
        />
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.featuresTitle || 'Our Core'} <span className="text-gradient">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent.featuresSubtitle || 'We deliver comprehensive solutions that combine cutting-edge technology with strategic business insights to drive measurable results.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                whileHover={{ y: -10 }}
                className="bg-white p-8 rounded-2xl shadow-premium hover:shadow-glow transition-all duration-300"
              >
                <div className="gradient-background w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  <feature.icon size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 gradient-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {pageContent.statsTitle || 'Results That Speak'}
            </h2>
            <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
              {pageContent.statsSubtitle || 'Our track record of success demonstrates the transformative power of our solutions.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="text-center"
              >
                <div className="text-4xl md:text-6xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-indigo-100 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {pageContent.testimonialsTitle || 'Client'} <span className="text-gradient">Success Stories</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent.testimonialsSubtitle || 'Hear from business leaders who have transformed their operations with our solutions.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className="glass-effect p-8 rounded-2xl"
              >
                <p className="text-gray-600 leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center space-x-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-gray-500 text-sm">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-6xl font-bold text-white">
              {pageContent.ctaTitle || 'Ready to Transform Your Business?'}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {pageContent.ctaSubtitle || 'Join the ranks of successful businesses that have revolutionized their operations with our AI-powered solutions and strategic consulting.'}
            </p>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0, 102, 255, 0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="gradient-background text-white px-12 py-4 rounded-xl font-bold text-xl shadow-premium hover:shadow-glow transition-all duration-300 inline-flex items-center space-x-3"
            >
              <span>{pageContent.ctaButtonText || 'Start Your Free Consultation'}</span>
              <ArrowRight size={24} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
